import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { logAnalyticsEvent } from '@/hooks/useAnalytics';

const SESSION_TRACKED_KEY = 'podbrief_session_tracked';

/**
 * Tracks a single "site_visit" event per browser session.
 * Uses sessionStorage to ensure we don't log duplicates on tab switches or navigations.
 */
export function useSessionTracking() {
  const { user, session } = useAuth();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    // Only track if we have an authenticated user and haven't tracked this session yet
    if (!user || !session) return;
    if (hasTrackedRef.current) return;

    // Check sessionStorage to see if we've already tracked this browser session
    const alreadyTracked = sessionStorage.getItem(SESSION_TRACKED_KEY);
    if (alreadyTracked) {
      hasTrackedRef.current = true;
      return;
    }

    // Log the site visit event
    logAnalyticsEvent({
      eventType: 'site_visit',
    }).then(() => {
      // Mark as tracked in both ref and sessionStorage
      hasTrackedRef.current = true;
      sessionStorage.setItem(SESSION_TRACKED_KEY, 'true');
    }).catch(() => {
      // Silently fail - session tracking should not disrupt user experience
    });
  }, [user, session]);
}
